import { useContext, useState } from "react";
import {
  Box,
  FormControlLabel,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import type { CapacityDecisionForContext } from "@thalamos/aspire-common";

import { apiHooks } from "~/api.js";
import {
  Button,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { ManagedLocationsContext } from "~/Contexts.js";

export type CapacityDecisionDialogProps = {
  patientId: string;
  onCancel: () => void;
  onConfirm: (decision: CapacityDecisionForContext) => void;
};

/**
 * Asked once when an MCA assessment is opened, because capacity is decision-specific
 * and only the residence decision moves a care home register's status. Without this
 * the coupling does not exist: an assessment of capacity to consent to surgery would
 * be indistinguishable from one about where somebody lives.
 *
 * The forms keep their own free-text `decisionOutline` for the narrative. This is the
 * machine-readable half, deliberately separate so dictated prose can never decide a
 * legal status.
 */
export function CapacityDecisionDialog(props: CapacityDecisionDialogProps) {
  const { managedLocations } = useContext(ManagedLocationsContext);

  // Fetched here rather than by the caller so it happens when the question is asked. Every
  // other form launch would otherwise pay for a residency it never reads.
  const [{ data: capacityHistory, loading: isResidencyLoading }] =
    apiHooks.capacityState.history(props.patientId);

  const openResidency = Array.isArray(capacityHistory)
    ? capacityHistory.find(
        (state) => state.state.residency && !state.state.residency.endDateTime,
      )?.state.residency
    : undefined;

  // Defaulting from the open residency makes the prompt a confirmation rather than data
  // entry, and means the decision names the home they actually live at.
  const currentCareHome = openResidency
    ? {
        id: openResidency.careHomeId,
        name:
          managedLocations.find(
            (location) => location.id === openResidency.careHomeId,
          )?.name ?? "their care home",
      }
    : undefined;

  const hasCurrentHome = !!currentCareHome;

  // The residency arrives asynchronously, so the default has to follow it rather than be
  // captured at mount: initialising from a value that is not there yet lands on "another
  // decision", the one choice that does not move the register, without anybody choosing
  // it. Explicit selection wins from then on.
  const [selected, setSelected] = useState<"residence" | "other" | null>(null);
  const choice = selected ?? (hasCurrentHome ? "residence" : "other");
  const [description, setDescription] = useState("");

  const canConfirm =
    !isResidencyLoading &&
    (choice === "residence" ? hasCurrentHome : description.trim().length > 0);

  return (
    <PopupDialog open onClose={props.onCancel}>
      <PopupDialogTitle
        closeDialog={props.onCancel}
        titleText="Which decision is this assessment about?"
      />

      <RadioGroup
        value={choice}
        onChange={(event) => {
          setSelected(event.target.value as "residence" | "other");
        }}
      >
        <FormControlLabel
          value="residence"
          disabled={!hasCurrentHome || isResidencyLoading}
          control={<Radio size="small" />}
          label={
            <Box>
              <Typography sx={{ fontSize: "0.9rem" }}>
                {hasCurrentHome
                  ? `Residence at ${currentCareHome!.name}`
                  : "Residence at a care home"}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#54596B" }}>
                {isResidencyLoading
                  ? "Checking whether they are on a register..."
                  : hasCurrentHome
                    ? "Taken from this person's open residency"
                    : "Unavailable — this person is not on a care home register"}
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          value="other"
          control={<Radio size="small" />}
          label={
            <Box>
              <Typography sx={{ fontSize: "0.9rem" }}>
                Another decision
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#54596B" }}>
                For example, whether to consent to elective surgery
              </Typography>
            </Box>
          }
        />
      </RadioGroup>

      {choice === "other" && (
        <Box sx={{ mt: 1 }}>
          <MuiTextField
            fullWidth
            size="small"
            label="Describe the decision"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
          <Typography sx={{ fontSize: "0.75rem", color: "#54596B", mt: 0.5 }}>
            This assessment will be recorded on the person&rsquo;s record and
            appear in their history, but it will not change their status on the
            resident register.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button label="Cancel" color="error" onClick={props.onCancel} />
        <Button
          label="Continue"
          disabled={!canConfirm}
          onClick={() => {
            props.onConfirm(
              choice === "residence"
                ? {
                    type: "residence",
                    careHomeId: currentCareHome!.id,
                  }
                : { type: "other", description: description.trim() },
            );
          }}
        />
      </Box>
    </PopupDialog>
  );
}
